import React, { useState, useRef, useEffect } from 'react';
import { Box, Card, CardContent, Container, Typography, Snackbar, Alert } from '@mui/material';
import QRCode from 'react-qr-code';
import { trackEvent } from '../utils/analytics';

interface FinalLinkQrProps {
  finalLink: string;
}

export const FinalLinkQr: React.FC<FinalLinkQrProps> = ({ finalLink }) => {
  const [copied, setCopied] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(finalLink);
      setCopied(true);
      trackEvent('link_gen_link_copied');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  useEffect(() => {
    if (qrRef.current && finalLink) {
      qrRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [finalLink]);

  return (
    <>
      <Container maxWidth="md" sx={{ mt: 3 }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          Click on the link to copy
        </Typography>
        <Card
          ref={qrRef}
          onClick={copyToClipboard}
          sx={{
            p: 2,
            mb: 3,
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            cursor: 'pointer',
            backgroundColor: '#f9f9f9',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: 3,
            },
            wordBreak: 'break-all',
            fontSize: '0.875rem',
          }}
        >
          {finalLink}
        </Card>
        <Typography variant="h2" sx={{ mb: 2 }}>
          Or use the QR Code below
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <QRCode value={finalLink} size={256} />
        </Box>
      </Container>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopied(false)} severity="success" sx={{ width: '100%' }}>
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default FinalLinkQr;
